import { isAreaSection } from "@/components/ui/tree/capture-tree/capture-tree-discriminator";
import { LightweightIElement } from "@faro-lotv/service-wires";

export type TreeDataElement = Pick<
  LightweightIElement,
  "id" | "type" | "typeHint" | "createdAt" | "labelIds" | "name" | "parentId"
>;

/**
 * Recursive tree structure of the project.
 * Each element contains a small subset of the IElement that is needed for the project tree component.
 */
export type TreeData = {
  /**
   * Unique id of this node in the tree.
   * If this node belongs to an IElement, this will be the GUID of that element.
   */
  id: string;

  /**
   * The label to show for this item in the tree.
   * If this node belongs to an IElement, this will be the name of that element.
   */
  label: string;

  /**
   * The information about the project element that maps to this tree node.
   * It will be `undefined` if this node is just a visual element (e.g., a folder).
   */
  element?: TreeDataElement;

  /**
   * All children of this element; recursive part of this data structure.
   *
   * If this is `null`, the element is a leaf of the tree.
   */
  children: TreeData[] | null;

  /**
   * As some levels of the tree will be removed during tree creation,
   * this will retain type information of the direct tree (from the original structure)
   */
  directParent?: Pick<LightweightIElement, "type" | "typeHint">;
};

type ElementToTreeDataParams = {
  /** element to convert */
  element: TreeDataElement & Pick<LightweightIElement, "name">;
  /** type to use for this element in the tree @default element.type */
  type?: LightweightIElement["type"];
  /** Array of labelIds attached to this element @default element.labelIds */
  labelIds?: LightweightIElement["labelIds"];
  /** typeHint to use for this element in the tree @default element.typeHint */
  typeHint?: LightweightIElement["typeHint"];
  /** parent element of the element */
  parentElement?: Pick<LightweightIElement, "type" | "typeHint">;
  /** ID to use for the element in the tree @default element.id */
  id?: LightweightIElement["id"];
  /** label to use for the element in the tree @default element.name */
  label?: string;
  /** children of the element @default [] */
  children?: TreeData[];
  /** Creation date of the element @default element.createdAt */
  createdAt?: LightweightIElement["createdAt"];
};

/**
 * @returns TreeData representation of the given element
 */
export function elementToTreeData({
  element,
  parentElement,
  type = element.type,
  labelIds = element.labelIds,
  typeHint = element.typeHint,
  id = element.id,
  label = element.name,
  children = [],
  createdAt = element.createdAt,
}: ElementToTreeDataParams): TreeData {
  return {
    id,
    label,
    element: {
      id,
      type,
      typeHint,
      labelIds: labelIds ? [...labelIds] : null,
      name: element.name,
      createdAt,
      parentId: element.parentId,
    },
    children: children.length > 0 || isAreaSection(element) ? children : null,
    directParent: parentElement
      ? {
          type: parentElement.type,
          typeHint: parentElement.typeHint,
        }
      : undefined,
  };
}
